/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Card, Grid, LinearProgress } from '@mui/material';
import { CardList, DialogForm, ICardActions, ICardColumns, IChipFilter, IChipsSettingProps, IDialogActions, IKanbanCardColumns, ISnackbarAlert, ISwitchInputProps, KanbanList, SelectField, SnackbarAlert, TextFieldInput, ToolBar } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { useTheme } from '@mui/material/styles';
import { AccessRoleDTO, AccessRoleFilterDTO, CommentDTO, CommentFilterDTO, ValueListDTO } from '../../../shared/interfaces';
import { LayoutDashboard } from '../../../shared/layouts';
import { CommentMaintenance } from './CommentMaintenance';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { useTranslation } from 'react-i18next';

interface ICommentProps {
    ownerId: any;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
    actionExecuted: boolean | false;
}

export const Comment: React.FC<ICommentProps> = ({ ownerId, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted, actionExecuted }) => {
    const [commentList, setCommentList] = useState<CommentDTO[]>([]);
    const [commentFilterDTO, setCommentFilterDTO] = useState<CommentFilterDTO>();

    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenSnackBar] = useState(false);
    const [isOpenMaintenanceLocal, setIsOpenMaintenanceLocal] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');
    const theme = useTheme();
    const { t } = useTranslation();

    useEffect(() => {
        setCommentFilterDTO({
            ...commentFilterDTO,
            ownerId: ownerId,
            commentary: '',
        } as CommentFilterDTO);

        handleGetPermission(ListId.infoSphere.commentId, setAccessRolePermissionDTO);
    }, [ownerId]);

    useEffect(() => {
        handleCommentGetAllViewAsync();
    },[commentFilterDTO]);

    useEffect(() => {
        if (actionExecuted) {
            handleCommentGetAllViewAsync();
        }
    }, [actionExecuted]);

    useEffect(() => {
        setEditRecord('');
    }, [isOpenMaintenance]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync', accessRoleFilterDTO, setActionExecuted, setLoading, undefined, setList,);
    }, []);

    const handleCommentGetAllViewAsync = useCallback(async () => {
        if (ownerId === undefined)
            return;
        if(accessRolePermissionDTO?.allowGetAllView == undefined)
            return;
        if(!accessRolePermissionDTO?.allowGetAllView) {
            showMessage(t('message.allowGetAllView'), 'info');
            return;
        }
        GenericService.GetAllViewAsync1('infosphere.Comment', commentFilterDTO, setActionExecuted, setLoading, setCommentList, showMessage, true);
    }, [commentFilterDTO, accessRolePermissionDTO]);

    const handleClearFilterSelectedClick = () => {
        setCommentFilterDTO({
            ...commentFilterDTO,
            ownerId: ownerId,
            commentary: ''
        } as CommentFilterDTO);
    };

    const handleSearchSelectedClick = () => {
        handleCommentGetAllViewAsync();
    };

    const handleNew = () => {
        if (!accessRolePermissionDTO?.allowAdd) {
            showMessage(t('message.allowAdd'), 'info');
            return;
        }
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setIsOpenMaintenanceLocal(true);
    };

    const handleViewDetatils = (data: string) => {
        if (!accessRolePermissionDTO?.allowGetById) {
            showMessage(t('message.allowGetById'), 'info');
            return;
        }
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setIsOpenMaintenanceLocal(true);
    };

    const handleDelete = (data: string) => {
        if (!accessRolePermissionDTO?.allowDelete) {
            showMessage(t('message.allowDelete'), 'info');
            return;
        }
        setNewRecord('');
        setEditRecord('');
        setDeleteRecord(data);
        setIsOpenMaintenanceLocal(true);
    };

    const actions: IDialogActions[] = [
        { id: 'close', text: t('infosphere.dialogActions.close'), icon: 'close_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    const cardActionsSelected: ICardActions[] = [
        { id: 'viewDetails', toolTip: t('infosphere.cardActionsSelected.viewDetails'), icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: t('infosphere.cardActionsSelected.delete'), icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFiltersSelected: IChipFilter[] = [
        { label: t('infosphere.chipFiltersSelected.totalRecords'), value: commentList.length },
        { label: t('infosphere.chipFiltersSelected.commentary'), value: commentFilterDTO?.commentary }
    ];

    const toolBarActionsSelected: IDialogActions[] = [
        { id: 'new', text: t('infosphere.toolBarActionsSelected.newComment'), icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const columns: ICardColumns[] = [
        { field: 'commentary', label: t('infosphere.cardColumns.commentary'), size: 8, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true, noWrap: false },
        { field: 'createdBy', label: t('infosphere.cardColumns.createdBy'), size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'creationDateTime', label: t('infosphere.cardColumns.creationDate'), size: 2, type: 'dateTime', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }
    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title={`${t('infosphere.comments.dialogForm')} - ${userInfo.user.fullName}`}
                icon='comment_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'xl'}
                actions={actions}
                loading={false}
            >
                <LayoutDashboard title={t('infosphere.layoutDashboard.title')}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={12}>
                            <Card sx={{ bgcolor: theme.palette.background.default, p: 1, height: '65vh' }}>
                                <ToolBar
                                    clickSearch={handleSearchSelectedClick}
                                    clickClearFilter={handleClearFilterSelectedClick}
                                    chipFilters={chipFiltersSelected}
                                    actions={toolBarActionsSelected}
                                >
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} md={12}>
                                            <TextFieldInput
                                                id='commentary'
                                                label={t('infosphere.textFieldInput.commentary')}
                                                placeholder={t('infosphere.textFieldInput.commentary')}
                                                setValue={setCommentFilterDTO}
                                                value={commentFilterDTO?.commentary}
                                                obj={commentFilterDTO}
                                                maxLength={200}
                                            />
                                        </Grid>
                                    </Grid>
                                </ToolBar>
                                {loading && (
                                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                                )}
                                <Box sx={{ display: 'flex', maxHeight: '80vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                                    <CardList
                                        columns={columns}
                                        rows={commentList}
                                        actions={cardActionsSelected}
                                        hasChildren={false}
                                        setRowExpanded={() => void undefined}
                                        cardSize={12}
                                    />
                                </Box>
                            </Card>
                        </Grid>
                    </Grid>
                </LayoutDashboard>
            </DialogForm >
            {newRecord && (
                <CommentMaintenance
                    ownerId={ownerId}
                    data={''}
                    setIsOpenMaintenance={setIsOpenMaintenanceLocal}
                    isOpenMaintenance={isOpenMaintenanceLocal}
                    setActionExecuted={setActionExecuted}
                />
            )}
            {editRecord && (
                <CommentMaintenance
                    ownerId={ownerId}
                    data={editRecord}
                    setIsOpenMaintenance={setIsOpenMaintenanceLocal}
                    isOpenMaintenance={isOpenMaintenanceLocal}
                    setActionExecuted={setActionExecuted}
                />
            )}
            {deleteRecord && (<Delete
                entity={'infosphere.comment'}
                data={deleteRecord}
                setIsOpenDiaglog={setIsOpenMaintenanceLocal}
                isOpenDialog={isOpenMaintenanceLocal}
                setActionExecuted={setActionExecuted}
                indexAttributeLabel={1}
            />)}
        </React.Fragment >
    );
};
